import {
  AES_MODE
} from "./chunk-YYOUJFPF.js";

// src/KDF.ts
var BYTE_TO_BIT = 8;
var KEY_SIZE = 32;
var NUM_ITERATIONS_V2 = 6e5;
var NUM_ITERATIONS_V1 = 1e4;
var derivePasswordBasedKeyV1 = async (password, salt) => derivePasswordBasedKey(Buffer.alloc(32, password, "utf-8"), salt, NUM_ITERATIONS_V1);
var derivePasswordBasedKeyV2 = async (password, salt) => derivePasswordBasedKey(Buffer.from(password, "utf-8"), salt, NUM_ITERATIONS_V2);
var derivePasswordBasedKey = async (password, salt, iterations) => {
  const algorithm = {
    name: "PBKDF2",
    salt,
    iterations,
    hash: "SHA-256"
  };
  const baseKey = await crypto.subtle.importKey("raw", password, algorithm.name, false, [
    "deriveKey"
  ]);
  return await crypto.subtle.deriveKey(
    algorithm,
    baseKey,
    { name: AES_MODE, length: KEY_SIZE * BYTE_TO_BIT },
    false,
    ["encrypt", "decrypt"]
  );
};

// src/AES.ts
import { buf2hex, hex2Bytes } from "@taquito/utils";
import { CustomError } from "@umami/utils";
import { differenceInMinutes } from "date-fns";
var SALT_SIZE = 32;
var IV_SIZE = 12;
var encrypt = async (data, password) => {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_SIZE));
  const derivedKey = await derivePasswordBasedKeyV2(password, salt);
  const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE));
  const encrypted = await crypto.subtle.encrypt(
    {
      name: AES_MODE,
      iv
    },
    derivedKey,
    Buffer.from(data, "utf-8")
  );
  return {
    iv: buf2hex(Buffer.from(iv)),
    salt: buf2hex(Buffer.from(salt)),
    data: buf2hex(Buffer.from(encrypted))
  };
};
var TOO_MANY_ATTEMPTS_ERROR = "Too many unsuccessful attempts. Please wait a few minutes before trying again.";
var decryptCommon = async (data, password, mode = "V2") => {
  const { iv, salt, data: encrypted } = data;
  const derivedKey = mode === "V2" ? await derivePasswordBasedKeyV2(password, hex2Bytes(salt)) : await derivePasswordBasedKeyV1(password, hex2Bytes(salt));
  const decrypted = await crypto.subtle.decrypt(
    {
      name: AES_MODE,
      iv: hex2Bytes(iv)
    },
    derivedKey,
    hex2Bytes(encrypted)
  );
  return Buffer.from(decrypted).toString("utf-8");
};
var decrypt = async (data, password, mode = "V2") => {
  try {
    if (getAttemptsCount() >= 3) {
      const minutesSinceLastAttempt = differenceInMinutes(
        /* @__PURE__ */ new Date(),
        new Date(localStorage.getItem("failedDecryptTime"))
      );
      if (minutesSinceLastAttempt < 5) {
        throw new CustomError(TOO_MANY_ATTEMPTS_ERROR);
      }
    }
    const decrypted = await decryptCommon(data, password, mode);
    setAttemptsCount(0);
    localStorage.removeItem("failedDecryptTime");
    return decrypted;
  } catch (err) {
    if (err?.message === TOO_MANY_ATTEMPTS_ERROR) {
      throw err;
    }
    setAttemptsCount(getAttemptsCount() + 1);
    localStorage.setItem("failedDecryptTime", (/* @__PURE__ */ new Date()).toISOString());
    throw new CustomError("Error decrypting data: Invalid password");
  }
};
var getAttemptsCount = () => Number(localStorage.getItem("passwordAttempts") || 0);
var setAttemptsCount = (count) => localStorage.setItem("passwordAttempts", String(count));
export {
  AES_MODE,
  TOO_MANY_ATTEMPTS_ERROR,
  decrypt,
  decryptCommon,
  derivePasswordBasedKeyV1,
  derivePasswordBasedKeyV2,
  encrypt
};
